import { useState, useEffect, useCallback } from 'react';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { Invoices } from './pages/Invoices';
import { Settings } from './pages/Settings';
import { t, translateBackendError } from './i18n';
import './styles.css';

type Page = 'dashboard' | 'orders' | 'invoices' | 'settings';

function getNavItems(): { key: Page; label: string }[] {
  return [
    { key: 'dashboard', label: t('nav_overview') },
    { key: 'orders', label: t('nav_orders') },
    { key: 'invoices', label: t('nav_invoices') },
    { key: 'settings', label: t('nav_settings') },
  ];
}

export default function App() {
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState<string | null>(null);

  const setError = useCallback((err: string | null) => {
    setErrorState(err ? translateBackendError(err) : null);
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [error, currentPage]);

  if (loading) {
    return (
      <div className="app-shell">
        <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="text-secondary">{t('loading')}</div>
        </div>
      </div>
    );
  }

  const navItems = getNavItems();

  return (
    <div className="app-shell">
      <nav className="nav-bar">
        <div className="nav-tabs">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => { setError(null); setCurrentPage(item.key); }}
              className={`nav-tab ${currentPage === item.key ? 'nav-tab--active' : ''}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {error && (
        <div className="page-error">
          <div className="banner banner--critical">
            <span className="banner-content">{error}</span>
            <button
              onClick={() => setError(null)}
              aria-label={t('hide')}
              className="banner-dismiss"
            >
              &#x2715;
            </button>
          </div>
        </div>
      )}

      {currentPage === 'dashboard' && <Dashboard onError={setError} />}
      {currentPage === 'orders' && <Orders onError={setError} />}
      {currentPage === 'invoices' && <Invoices onError={setError} />}
      {currentPage === 'settings' && <Settings onError={setError} />}
    </div>
  );
}
